import type { JSX } from 'react';
import type {
  CompanyAccountResource,
  CompanyDetailResource,
  ContactResource,
  UpdateCompanyBody,
} from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Bell, Ellipsis, Pencil, PinOff, Plus } from 'lucide-react';
import { useState } from 'react';
import { ActivityComposer, ActivityFeed } from '../components/crm/Activity';
import { EditCompanyDialog } from '../components/crm/CompanyDialog';
import { ContactDialog, type ContactDialogMode } from '../components/crm/ContactDialog';
import { CreateDealDialog } from '../components/crm/DealDialog';
import { LinkAccountDialog, PinnedNoteDialog } from '../components/crm/MiscDialogs';
import { ReminderDialog, type ReminderDialogMode } from '../components/crm/ReminderDialog';
import {
  backLinkClass,
  DetailHead,
  MiniCell,
  MiniList,
  MiniRow,
  useCompany,
  useCrmRefresh,
} from '../components/crm/shared';
import { PageChrome } from '../components/page-chrome';
import {
  COMPANY_STATUS_BADGE,
  COMPANY_STATUS_LABEL,
  crmSend,
  crmSendEmpty,
  errorMessage,
  eurOrDash,
  formatDate,
  formatInstantDate,
  formatLongDate,
  formatReminderDue,
  MODEL_LABEL,
  ORIGIN_LABEL,
  parisDateOf,
  REMINDER_TYPE_LABEL,
  ROLE_LABEL,
  STAGE_LABEL,
  stageBadgeVariant,
} from '../lib/crm';
import { formatEurCents, initials } from '../lib/format';
import { Callout } from '@/components/ui/alert';
import { CellUser } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardBar, CardContent, CardHead } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoadingState } from '@/components/ui/empty';
import { KvList, KvRow } from '@/components/ui/kv';
import { Row4, Split21, Stack } from '@/components/ui/layout-grid';
import { formatMonthLabel } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { clientStatusLabel, StatusBadge } from '@/components/ui/status-tag';
import {
  Cell2,
  rowLinkClass,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';
import { useCan } from '../lib/me';
import { AppointmentsCard } from '../components/crm/Appointments';
import { CompanyRevenue } from '../components/crm/Revenue';

type Section = 'activity' | 'contacts' | 'deals' | 'accounts';

function isOpenStage(stage: string): boolean {
  return stage !== 'won' && stage !== 'lost';
}

function ContactsTable({
  company,
  onEdit,
}: {
  company: CompanyDetailResource;
  onEdit: (contact: ContactResource) => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const makePrimary = useMutation({
    mutationFn: (contactId: string) => {
      const body: UpdateCompanyBody = { primaryContactId: contactId };
      return crmSend<CompanyDetailResource>('PATCH', `/companies/${company.id}`, body);
    },
    onSuccess: () => {
      refresh();
    },
  });
  return (
    <>
      {makePrimary.isError ? (
        <Callout variant="destructive" className="m-3">
          {errorMessage(makePrimary.error, 'Échec du changement de contact principal.')}
        </Callout>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead kind="name">Contact</TableHead>
            <TableHead>Rôles</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Téléphone</TableHead>
            <TableHead kind="act">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {company.contacts.map((contact) => {
            const name = `${contact.firstName} ${contact.lastName}`;
            return (
              <TableRow key={contact.id}>
                <TableCell kind="name">
                  <CellUser initials={initials(name)}>
                    <Cell2 primary={name} secondary={contact.jobTitle ?? undefined} />
                  </CellUser>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {contact.primary ? <Badge variant="neutral">Principal</Badge> : null}
                    {contact.roles.map((role) => (
                      <Tag key={role}>{ROLE_LABEL[role]}</Tag>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {contact.email !== null ? (
                    <a
                      className="underline underline-offset-[3px]"
                      href={`mailto:${contact.email}`}
                    >
                      {contact.email}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell kind="mono">{contact.phone ?? '—'}</TableCell>
                <TableCell kind="act">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Actions pour ${name}`}
                      >
                        <Ellipsis aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          onEdit(contact);
                        }}
                      >
                        Modifier
                      </DropdownMenuItem>
                      {contact.primary ? null : (
                        <DropdownMenuItem
                          onSelect={() => {
                            makePrimary.mutate(contact.id);
                          }}
                        >
                          Définir comme contact principal
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
          {company.contacts.length === 0 ? (
            <TableEmptyRow colSpan={5}>Aucun contact.</TableEmptyRow>
          ) : null}
        </TableBody>
      </Table>
    </>
  );
}

function DealsTable({ company }: { company: CompanyDetailResource }): JSX.Element {
  const navigate = useNavigate();
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead kind="name">Affaire</TableHead>
          <TableHead>Étape</TableHead>
          <TableHead kind="num">Mise en place</TableHead>
          <TableHead kind="num">Abonnement / mois</TableHead>
          <TableHead>Date</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {company.deals.map((deal) => {
          const closed = deal.closedOn !== null;
          const date = closed ? deal.closedOn : deal.expectedCloseDate;
          return (
            <TableRow
              key={deal.id}
              onClick={() => void navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })}
            >
              <TableCell kind="name">
                <Cell2
                  primary={
                    <Link
                      to="/deals/$dealId"
                      params={{ dealId: deal.id }}
                      className={rowLinkClass}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {deal.title}
                    </Link>
                  }
                  secondary={MODEL_LABEL[deal.model]}
                />
              </TableCell>
              <TableCell>
                <Badge variant={stageBadgeVariant(deal.stage)}>{STAGE_LABEL[deal.stage]}</Badge>
              </TableCell>
              <TableCell kind="num">{eurOrDash(deal.setup)}</TableCell>
              <TableCell kind="num">{eurOrDash(deal.monthly)}</TableCell>
              <TableCell>
                {date !== null ? (
                  <Cell2
                    primary={formatDate(date)}
                    primaryMono
                    secondary={
                      deal.stage === 'won'
                        ? 'Signée'
                        : deal.stage === 'lost'
                          ? 'Perdue'
                          : 'Signature prévue'
                    }
                  />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
        {company.deals.length === 0 ? (
          <TableEmptyRow colSpan={5}>Aucune affaire.</TableEmptyRow>
        ) : null}
      </TableBody>
    </Table>
  );
}

function AccountsTable({ company }: { company: CompanyDetailResource }): JSX.Element {
  const refresh = useCrmRefresh();
  const [unlinking, setUnlinking] = useState<CompanyAccountResource | null>(null);
  const unlink = useMutation({
    mutationFn: (clientId: string) =>
      crmSendEmpty('DELETE', `/companies/${company.id}/accounts/${clientId}`),
    onSuccess: () => {
      setUnlinking(null);
      refresh({ clients: true });
    },
  });
  return (
    <>
      {unlink.isError ? (
        <Callout variant="destructive" className="m-3">
          {errorMessage(unlink.error, 'Échec du détachement du compte.')}
        </Callout>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead kind="name">Compte facturé</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead kind="num">Usage IA du mois</TableHead>
            <TableHead kind="num">Plafond de sécurité</TableHead>
            <TableHead>Créé le</TableHead>
            <TableHead kind="act">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {company.accounts.map((account) => (
            <TableRow key={account.id}>
              <TableCell kind="name">
                <Cell2
                  primary={
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: account.id }}
                      className={rowLinkClass}
                    >
                      {account.name}
                    </Link>
                  }
                  secondary={account.billingEmail}
                />
              </TableCell>
              <TableCell>
                <StatusBadge status={clientStatusLabel(account.status)} />
              </TableCell>
              <TableCell kind="num">{formatEurCents(account.monthUsage)}</TableCell>
              <TableCell kind="num">{formatEurCents(account.safetyCap)}</TableCell>
              <TableCell kind="mono">{formatInstantDate(account.createdAt)}</TableCell>
              <TableCell kind="act">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Actions pour ${account.name}`}
                    >
                      <Ellipsis aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => {
                        setUnlinking(account);
                      }}
                    >
                      Détacher de l’entreprise
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {company.accounts.length === 0 ? (
            <TableEmptyRow colSpan={6}>Aucun compte facturé rattaché.</TableEmptyRow>
          ) : null}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={unlinking !== null}
        onClose={() => {
          setUnlinking(null);
        }}
        onConfirm={() => {
          if (unlinking !== null) {
            unlink.mutate(unlinking.id);
          }
        }}
        title="Détacher le compte facturé ?"
        subtitle={
          unlinking !== null
            ? `${unlinking.name} reste un compte facturé actif, mais n’est plus rattaché à ${company.name}.`
            : undefined
        }
        confirmLabel="Détacher"
        busy={unlink.isPending}
        variant="destructive"
      />
    </>
  );
}

export function CompanyDetailPage({ companyId }: { companyId: string }): JSX.Element {
  const company = useCompany(companyId);
  const refresh = useCrmRefresh();
  const [section, setSection] = useState<Section>('activity');
  const canSeeAccounts = useCan('operations');
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [reminder, setReminder] = useState<ReminderDialogMode | null>(null);
  const [contactMode, setContactMode] = useState<ContactDialogMode | null>(null);
  const [editing, setEditing] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [linking, setLinking] = useState(false);

  const unpin = useMutation({
    mutationFn: () => {
      const body: UpdateCompanyBody = { pinnedNote: null };
      return crmSend<CompanyDetailResource>('PATCH', `/companies/${companyId}`, body);
    },
    onSuccess: () => {
      refresh();
    },
  });

  const data = company.data;
  if (data === undefined) {
    return (
      <>
        <PageChrome crumbs={[{ label: 'Entreprises', to: '/companies' }, { label: 'Fiche' }]} />
        {company.isError ? (
          <Callout variant="destructive">Impossible de charger l’entreprise.</Callout>
        ) : (
          <LoadingState />
        )}
      </>
    );
  }

  const openDeals = data.deals.filter((deal) => isOpenStage(deal.stage));
  const singleOpen = openDeals.length === 1 ? openDeals[0] : undefined;
  const primary = data.contacts.find((contact) => contact.primary) ?? null;
  const subtitle = [
    [data.sector, data.address]
      .filter((part): part is string => part !== null && part !== '')
      .join(', '),
    data.clientSince !== null
      ? `Client depuis le ${formatLongDate(parisDateOf(new Date(data.clientSince)))}`
      : null,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .map((part) => `${part}.`)
    .join(' ');

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Entreprises', to: '/companies' }, { label: data.name }]}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setReminder({ kind: 'create', companyId: data.id });
              }}
            >
              <Bell aria-hidden="true" />
              Programmer un rappel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setCreatingDeal(true);
              }}
            >
              <Plus aria-hidden="true" />
              Nouvelle affaire
            </Button>
          </>
        }
      />
      <DetailHead
        back={
          <Link to="/companies" className={backLinkClass}>
            <ArrowLeft aria-hidden="true" />
            Entreprises
          </Link>
        }
        title={data.name}
        badges={
          <>
            <Badge variant={COMPANY_STATUS_BADGE[data.status]}>
              {COMPANY_STATUS_LABEL[data.status]}
            </Badge>
            {subtitle !== '' ? (
              <span className="text-[13px] leading-[18px] text-muted-foreground">{subtitle}</span>
            ) : null}
          </>
        }
      />

      <Row4 aria-label="Indicateurs de l’entreprise">
        <StatCard
          label="Abonnement signé"
          value={eurOrDash(data.stats.signedMonthly)}
          foot="par mois, HT"
        />
        <StatCard
          label={data.stats.openDeals > 1 ? 'Affaires ouvertes' : 'Affaire ouverte'}
          value={eurOrDash(data.stats.openWeighted)}
          foot={
            singleOpen !== undefined
              ? `pondéré, ${STAGE_LABEL[singleOpen.stage]}`
              : `pondéré, ${data.stats.openDeals} affaire${data.stats.openDeals > 1 ? 's' : ''} ouverte${data.stats.openDeals > 1 ? 's' : ''}`
          }
        />
        {data.stats.monthUsage !== null ? (
          <StatCard
            label="Usage IA du mois"
            value={formatEurCents(data.stats.monthUsage)}
            foot={`${formatMonthLabel(data.stats.month)}, HT, ${data.accounts.length} compte${data.accounts.length > 1 ? 's' : ''}`}
          />
        ) : null}
      </Row4>

      <Split21 className="items-start">
        <Card>
          <CardBar
            start={
              <TableTabs<Section>
                aria-label="Sections de la fiche"
                value={section}
                onValueChange={setSection}
                items={[
                  { value: 'activity', label: 'Activité', count: data.activities.length },
                  { value: 'contacts', label: 'Contacts', count: data.contacts.length },
                  { value: 'deals', label: 'Affaires', count: data.deals.length },
                  // Comptes facturés : administrateurs seulement (lot 20).
                  ...(canSeeAccounts
                    ? [
                        {
                          value: 'accounts' as const,
                          label: 'Comptes facturés',
                          count: data.accounts.length,
                        },
                      ]
                    : []),
                ]}
              />
            }
            end={
              section === 'contacts' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setContactMode({
                      kind: 'create',
                      companyId: data.id,
                      companyName: data.name,
                      primaryName:
                        primary !== null ? `${primary.firstName} ${primary.lastName}` : null,
                    });
                  }}
                >
                  Ajouter un contact
                </Button>
              ) : section === 'accounts' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLinking(true);
                  }}
                >
                  Lier un compte existant
                </Button>
              ) : undefined
            }
          />
          {section === 'activity' ? (
            <>
              <ActivityComposer companyId={data.id} deals={data.deals} />
              <ActivityFeed activities={data.activities} showDeal label="Journal d’activité" />
            </>
          ) : null}
          {section === 'contacts' ? (
            <ContactsTable
              company={data}
              onEdit={(contact) => {
                setContactMode({ kind: 'edit', contact });
              }}
            />
          ) : null}
          {section === 'deals' ? <DealsTable company={data} /> : null}
          {section === 'accounts' && canSeeAccounts ? <AccountsTable company={data} /> : null}
        </Card>

        <Stack className="gap-5">
          <AppointmentsCard
            companyId={data.id}
            dealId={
              data.deals.filter((d) => d.stage !== 'won' && d.stage !== 'lost').length === 1
                ? (data.deals.find((d) => d.stage !== 'won' && d.stage !== 'lost')?.id ?? null)
                : null
            }
            contact={primary}
            appointments={data.appointments}
            types={data.appointmentTypes}
          />

          <Card>
            <CardHead
              title="Note épinglée"
              actions={
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={data.pinnedNote !== null ? 'Modifier la note' : 'Écrire une note'}
                    onClick={() => {
                      setEditingNote(true);
                    }}
                  >
                    <Pencil aria-hidden="true" />
                  </Button>
                  {data.pinnedNote !== null ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Désépingler la note"
                      disabled={unpin.isPending}
                      onClick={() => {
                        unpin.mutate();
                      }}
                    >
                      <PinOff aria-hidden="true" />
                    </Button>
                  ) : null}
                </>
              }
            />
            <CardContent>
              {data.pinnedNote !== null ? (
                <p className="m-0 whitespace-pre-line text-[13px] leading-5">{data.pinnedNote}</p>
              ) : (
                <p className="m-0 text-[13px] text-muted-foreground">Aucune note épinglée.</p>
              )}
              {unpin.isError ? (
                <p className="m-0 mt-2 text-caption text-danger-fg">
                  {errorMessage(unpin.error, 'Échec du désépinglage.')}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHead
              title="Rappels"
              description={
                data.reminders.length === 0
                  ? 'Aucun rappel ouvert'
                  : `${data.reminders.length} à venir`
              }
              actions={
                <Button asChild variant="ghost" size="sm">
                  <Link to="/todo">Tout voir</Link>
                </Button>
              }
            />
            <CardContent>
              <MiniList>
                {data.reminders.map((item) => (
                  <MiniRow key={item.id}>
                    <MiniCell
                      primary={item.subject}
                      secondary={[
                        REMINDER_TYPE_LABEL[item.type],
                        item.contactName ?? item.dealTitle,
                      ]
                        .filter((part): part is string => part !== null)
                        .join(', ')}
                    />
                    <span className="flex shrink-0 items-center gap-1">
                      {item.timing === 'late' ? <Badge variant="warning">En retard</Badge> : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        className="font-mono"
                        aria-label={`Reporter : ${item.subject}`}
                        onClick={() => {
                          setReminder({ kind: 'edit', reminder: item });
                        }}
                      >
                        {formatReminderDue(item)}
                      </Button>
                    </span>
                  </MiniRow>
                ))}
              </MiniList>
            </CardContent>
          </Card>

          <Card>
            <CardHead
              title="Informations"
              actions={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Modifier les informations"
                  onClick={() => {
                    setEditing(true);
                  }}
                >
                  <Pencil aria-hidden="true" />
                </Button>
              }
            />
            <CardContent>
              <KvList>
                <KvRow label="Raison sociale">{data.legalName ?? '—'}</KvRow>
                <KvRow label="SIREN" mono>
                  {data.siren ?? '—'}
                </KvRow>
                <KvRow label="Code NAF" mono>
                  {data.nafCode ?? '—'}
                </KvRow>
                <KvRow label="Effectif">{data.headcount ?? '—'}</KvRow>
                <KvRow label="Dernier CA connu">
                  <CompanyRevenue companyId={data.id} />
                </KvRow>
                <KvRow label="Adresse">{data.address ?? '—'}</KvRow>
                <KvRow label="Secteur">{data.sector ?? '—'}</KvRow>
                <KvRow label="Origine">{ORIGIN_LABEL[data.origin]}</KvRow>
                <KvRow label="Créée le" mono>
                  {formatInstantDate(data.createdAt)}
                </KvRow>
              </KvList>
            </CardContent>
          </Card>
        </Stack>
      </Split21>

      <CreateDealDialog
        open={creatingDeal}
        companyId={data.id}
        onClose={() => {
          setCreatingDeal(false);
        }}
      />
      <ReminderDialog
        mode={reminder}
        onClose={() => {
          setReminder(null);
        }}
      />
      <ContactDialog
        mode={contactMode}
        onClose={() => {
          setContactMode(null);
        }}
      />
      <EditCompanyDialog
        company={editing ? data : null}
        onClose={() => {
          setEditing(false);
        }}
      />
      <PinnedNoteDialog
        company={editingNote ? data : null}
        onClose={() => {
          setEditingNote(false);
        }}
      />
      <LinkAccountDialog
        open={linking}
        companyId={data.id}
        onClose={() => {
          setLinking(false);
        }}
      />
    </>
  );
}
